import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addJobRequired,
  getJobRequiredDataById,
  updateJobRequired,
} from "model/JobRequiredModel";

/* -------------------- Yup Schema -------------------- */
const jobRequiredSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Name is required"),

  forJob: Yup.number()
    .oneOf([0, 1], "For Job is required")
    .required("For Job is required"),

  forClient: Yup.number()
    .oneOf([0, 1], "For Client is required")
    .required("For Client is required"),
});

const JobRequiredAddEditModal = ({
  setJobRequiredAddEditModalOpen,
  jobRequiredAddEditModalOpen,
  getJobRequiredList,
  itemId,
  setItemId,
  setFormData,
  formData,
  initialFormState,
}) => {
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});

  const closeModal = () => {
    setJobRequiredAddEditModalOpen(false);
    setErrors({});
    setFormData(initialFormState);
    setItemId?.(null);
    setFormKey((k) => k + 1);
  };

  /* -------------------- Change Handler -------------------- */
  const handleChange = async (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    if (!jobRequiredSchema.fields[name]) return;

    try {
      await Yup.reach(jobRequiredSchema, name).validate(fieldValue);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await jobRequiredSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const params = {
      ...formData,
      status: formData?.status ? "active" : "deactive",
    };

    let response;
    if (itemId) {
      response = await updateJobRequired(itemId, params);
    } else {
      response = await addJobRequired(params);
    }

    if (response?.success) {
      toast.success(response?.message);
      closeModal();
      getJobRequiredList();
    } else {
      toast.error(response?.message);
    }
  };

  /* -------------------- Edit Fetch -------------------- */
  const getFetchJobRequiredById = async (id) => {
    const response = await getJobRequiredDataById({ Id: id });
    if (response?.data) {
      setFormData({
        ...response?.data,
        status:
          response?.data?.status === "active",
        forClient: response?.data?.forClient,
        forJob: response?.data?.forJob,
      });
      setFormKey((k) => k + 1);
    }
  };

  useEffect(() => {
    if (itemId) {
      getFetchJobRequiredById(itemId);
    }
  }, [itemId]);

  return (
    <Modal
      isOpen={jobRequiredAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
      zIndex={5000}
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{itemId? 'Edit' : 'Add'} Job Required</h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          {/* Name */}
          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              name="name"
              defaultValue={formData?.name}
              onChange={handleChange}
            />
            {errors.name && (
              <small className="text-danger">
                {errors.name}
              </small>
            )}
          </FormGroup>
           {/* For Client */}
           <FormGroup>
            <Label>For Client</Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Choose For Client</span>
              <div>
                <Input
                  type="radio"
                  name="forClient"
                  defaultChecked={formData?.forClient === 1}
                  onChange={() =>
                    setFormData((p) => ({ ...p, forClient: 1 }))
                  }
                />{" "}
                Yes
                <Input
                  type="radio"
                  name="forClient"
                  defaultChecked={formData?.forClient === 0}
                  onChange={() =>
                    setFormData((p) => ({ ...p, forClient: 0 }))
                  }
                  className="ms-3"
                />{" "}
                No
              </div>
            </div>
            {errors.forClient && (
              <small className="text-danger">{errors.forClient}</small>
            )}
          </FormGroup>

          {/* For Job */}
          <FormGroup>
            <Label>For Job</Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Choose For Job</span>
              <div>
                <Input
                  type="radio"
                  name="forJob"
                  defaultChecked={formData?.forJob === 1}
                  onChange={() =>
                    setFormData((p) => ({ ...p, forJob: 1 }))
                  }
                />{" "}
                Yes
                <Input
                  type="radio"
                  name="forJob"
                  defaultChecked={formData?.forJob === 0}
                  onChange={() =>
                    setFormData((p) => ({ ...p, forJob: 0 }))
                  }
                  className="ms-3"
                />{" "}
                No
              </div>
            </div>
            {errors.forJob && (
              <small className="text-danger">{errors.forJob}</small>
            )}
          </FormGroup>

         

          {/* Status */}
          <FormGroup>
            <Label>Status</Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Choose Status</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData?.status ? "Active" : "Deactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    type="switch"
                    name="status"
                    defaultChecked={formData?.status}
                    onChange={handleChange}
                    className="form-check-input-padding"
                  />
                </div>
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
            {itemId? 'Update' : 'Save'}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default JobRequiredAddEditModal;
